import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, PhoneCall } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Home & Auto", "Life & Health", "Medicaid"];
const EMPTY = { name: "", email: "", phone: "", insurance_type: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.insurance_type) {
      toast.error("Please choose a coverage type.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Request received — Renee will reach out personally within one business day.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call 404-703-2343.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute left-[-10%] top-1/3 h-[28rem] w-[28rem] rounded-full bg-amber-500/[0.07] blur-[120px]" />
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:px-12">
        <Reveal>
          <Eyebrow testId="quote-eyebrow">Start the conversation</Eyebrow>
          <h2
            data-testid="quote-heading"
            className="font-serif text-2xl tracking-tight text-[color:var(--ns-ink)] sm:text-3xl lg:text-4xl"
          >
            Tell us what matters. <em className="text-[color:var(--ns-gold)]">We'll protect it.</em>
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-[color:var(--ns-muted)] sm:text-lg">
            No call centers, no pressure. Your request goes straight to Renee's desk — and you'll
            hear back from a real person who knows Atlanta.
          </p>
          <a
            href="tel:+14047032343"
            data-testid="quote-call-link"
            className="mt-9 inline-flex items-center gap-3 rounded-2xl border border-[color:var(--ns-line)] bg-[color:var(--ns-card)] px-6 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-[color:var(--ns-gold)]/40"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--ns-gold)]/15 text-[color:var(--ns-gold)]">
              <PhoneCall size={18} />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-[0.2em] text-[color:var(--ns-muted)]">Prefer to talk?</span>
              <span className="block font-mono text-lg text-[color:var(--ns-ink)]">404-703-2343</span>
            </span>
          </a>
        </Reveal>

        <Reveal delay={0.12}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="spotlight-card rounded-3xl p-7 sm:p-9"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="quote-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ns-muted)]">
                  Full name
                </label>
                <input
                  id="quote-name"
                  data-testid="quote-name-input"
                  className="ns-input"
                  placeholder="Jordan Avery"
                  value={form.name}
                  onChange={set("name")}
                  required
                  minLength={2}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="quote-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ns-muted)]">
                  Phone
                </label>
                <input
                  id="quote-phone"
                  data-testid="quote-phone-input"
                  className="ns-input"
                  type="tel"
                  placeholder="404-555-0100"
                  value={form.phone}
                  onChange={set("phone")}
                  required
                  minLength={7}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quote-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ns-muted)]">
                  Email
                </label>
                <input
                  id="quote-email"
                  data-testid="quote-email-input"
                  className="ns-input"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={set("email")}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quote-type" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ns-muted)]">
                  Coverage type
                </label>
                <select
                  id="quote-type"
                  data-testid="quote-type-select"
                  className="ns-input"
                  value={form.insurance_type}
                  onChange={set("insurance_type")}
                  required
                >
                  <option value="" disabled>
                    Choose a coverage line…
                  </option>
                  {TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="quote-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[color:var(--ns-muted)]">
                  What should we know?
                </label>
                <textarea
                  id="quote-message"
                  data-testid="quote-message-input"
                  className="ns-input min-h-[120px] resize-y"
                  placeholder="A new home, a growing family, a parent's Medicaid renewal…"
                  value={form.message}
                  onChange={set("message")}
                  maxLength={2000}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="quote-submit-btn"
              disabled={sending}
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--ns-gold)] px-7 py-4 font-semibold text-[#0B0F17] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/25 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
              {sending ? "Sending…" : "Request My Quote"}
            </button>
            <p className="mt-4 text-center text-xs text-[color:var(--ns-muted)]">
              Your details go only to Hunt Shield Insurance Group. Never sold, never shared.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
